import * as THREE from 'three';
import * as CANNON from 'cannon-es';
import PlayerInventory from "../inventory/PlayerInventory.js";
import Pistol from "../weapons/Pistol.js";
import { PlayerModel } from "./PlayerModel.js";

// Import new punch and strength skill systems
import { PunchAttack } from '../combat/melee/PunchAttack.js';
import { StrengthSkill } from '../skills/StrengthSkill.js';

export class PlayerController {
    constructor(mesh, body, scene) {
        this.mesh = mesh;
        this.body = body;
        
        // Make controller globally accessible
        window.playerController = this;
        
        // Store reference to player model if it exists
        this.playerModel = null;
        
        // Check if mesh is actually our PlayerModel
        if (mesh instanceof PlayerModel) {
            this.playerModel = mesh;
            console.log("Using enhanced PlayerModel");
        } else {
            console.log("Using basic mesh for player");
        }

        // Movement state
        this.moveState = {
            forward: false,
            backward: false,
            left: false,
            right: false
        };
        
        // Player properties
        this._canJump = true;
        this.moveSpeed = 0.15;
        this.jumpHeight = 1.5;
        
        // Health system properties
        this.maxHealth = 100;
        this.currentHealth = 100;
        this.isInvulnerable = false;
        this.invulnerabilityTime = 1000; // 1 second invulnerability after taking damage
        this.lastDamageTime = 0;
        this.isDead = false;
        
        // Create health UI elements
        this.createHealthUI();
        
        // Climbing configuration
        this.maxAutoClimbHeight = 1.1;  // Maximum height for auto-climbing (exactly 1.1 blocks)
        this.minClimbThreshold = 0.1;   // Minimum height difference to consider it a climb
        this.maxStepDistance = 1.0;     // Maximum distance to move in a single step
        this.maxManualClimbHeight = 2.0; // Maximum height for manual jumps
        
        // Player dimensions
        this.playerHeight = 2.0;  // Player is 2 blocks tall
        this.playerWidth = 1.0;   // Player is 1 block wide
        this.footOffset = 2.5;    // Increase from 1.5 to 2.5 for more ground clearance
        
        // Climb cooldown to prevent spamming
        this.lastClimbTime = 0;
        this.climbCooldown = 150;  // 150ms cooldown between climbs
        
        // Movement feedback
        this.debugBlocked = false;
        this.blockReason = "";
        
        // Position smoothing for camera shake prevention
        this.lastY = 0;
        this.targetY = 0;
        this.positionSmoothing = 0.3; // Lower value = smoother transitions
        
        // Add jump smoothing variables
        this.jumpState = {
            jumping: false,
            startTime: 0,
            startY: 0,
            targetY: 0,
            duration: 300, // ms
            jumpType: 'none' // 'none', 'auto', 'manual'
        };
        this.lastJumpTime = 0;
        this.jumpCooldown = 250; // Minimum time between jumps
        this.isApproachingStep = false;
        this.predictedStepHeight = 0;
        
        // Save reference to scene for weapon models
        this.scene = scene;
        
        // Track movement for animation
        this.isMoving = false;
        this.lastPosition = new THREE.Vector3();
        if (this.mesh) {
            this.lastPosition.copy(this.mesh.position);
        }
        
        // Direction the player is facing
        this.facingDirection = new THREE.Vector3(0, 0, -1);
        
        // Initialize inventory system with 4 slots
        this.inventory = new PlayerInventory(4);
        
        // Initialize strength skill for melee attacks
        this.strengthSkill = new StrengthSkill();
        
        // Initialize punch as default attack
        this.punchAttack = null;
        
        // Initialize with default weapons if scene is available
        if (this.scene) {
            this.initializeDefaultEquipment();
            console.log("Default equipment initialized during construction");
        } else {
            console.warn("Scene not available during PlayerController construction, equipment will be initialized later");
        }
    }

    // Create health bar UI
    createHealthUI() {
        // Create health bar container
        this.healthContainer = document.createElement('div');
        this.healthContainer.style.cssText = `
            position: fixed;
            bottom: 20px;
            left: 20px;
            width: 200px;
            height: 25px;
            background-color: #333;
            border: 2px solid #666;
            border-radius: 5px;
            z-index: 1000;
        `;
        
        // Create health bar
        this.healthBar = document.createElement('div');
        this.healthBar.style.cssText = `
            width: 100%;
            height: 100%;
            background-color: #3c3;
            border-radius: 3px;
            transition: width 0.3s;
        `;
        
        // Create health text
        this.healthText = document.createElement('div');
        this.healthText.style.cssText = `
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            color: white;
            font-family: Arial, sans-serif;
            font-size: 14px;
            font-weight: bold;
            text-shadow: 1px 1px 1px #000;
        `;
        this.healthText.textContent = `${this.currentHealth}/${this.maxHealth}`;
        
        // Assemble and add to DOM
        this.healthContainer.appendChild(this.healthBar);
        this.healthContainer.appendChild(this.healthText);
        document.body.appendChild(this.healthContainer);
        
        // Initialize health display
        this.updateHealthUI();
    }
    
    // Update the health bar UI
    updateHealthUI() {
        if (!this.healthBar || !this.healthText) return;
        
        // Calculate health percentage
        const healthPercent = (this.currentHealth / this.maxHealth) * 100;
        
        // Update health bar width
        this.healthBar.style.width = `${healthPercent}%`;
        
        // Update text
        this.healthText.textContent = `${Math.ceil(this.currentHealth)}/${this.maxHealth}`;
        
        // Change color based on health
        if (healthPercent <= 25) {
            this.healthBar.style.backgroundColor = '#f33'; // Red for low health
        } else if (healthPercent <= 50) {
            this.healthBar.style.backgroundColor = '#fc3'; // Yellow for medium health
        } else {
            this.healthBar.style.backgroundColor = '#3c3'; // Green for good health
        }
        
        // Flash effect when taking damage
        if (performance.now() - this.lastDamageTime < 200) {
            this.healthContainer.style.borderColor = '#f00';
        } else {
            this.healthContainer.style.borderColor = '#666';
        }
    }
    
    // Take damage from any source
    takeDamage(amount, source = 'unknown') {
        // Check if player is currently invulnerable
        const now = performance.now();
        if (this.isInvulnerable && now - this.lastDamageTime < this.invulnerabilityTime) {
            return false;
        }
        
        // Apply damage
        this.currentHealth = Math.max(0, this.currentHealth - amount);
        this.lastDamageTime = now;
        this.isInvulnerable = true;
        
        // Update UI
        this.updateHealthUI();
        
        console.log(`Player took ${amount} damage from ${source}. Health: ${this.currentHealth}/${this.maxHealth}`);
        
        // Create damage indicator visual
        this.showDamageIndicator();
        
        // Check for death
        if (this.currentHealth <= 0 && !this.isDead) {
            this.handleDeath();
            return true;
        }
        
        // Reset invulnerability after delay
        setTimeout(() => {
            this.isInvulnerable = false;
        }, this.invulnerabilityTime);
        
        return true;
    }
    
    // Visual indicator when taking damage
    showDamageIndicator() {
        // Create a full-screen red flash effect
        const damageFlash = document.createElement('div');
        damageFlash.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background-color: rgba(255, 0, 0, 0.3);
            z-index: 999;
            pointer-events: none;
        `;
        
        // Add to DOM
        document.body.appendChild(damageFlash);
        
        // Fade out and remove
        setTimeout(() => {
            damageFlash.style.transition = 'opacity 0.5s';
            damageFlash.style.opacity = '0';
            setTimeout(() => {
                damageFlash.remove();
            }, 500);
        }, 100);
    }
    
    // Handle player death
    handleDeath() {
        this.isDead = true;
        console.log("Player died!");
        
        // Exit pointer lock mode to show cursor
        if (document.pointerLockElement) {
            document.exitPointerLock();
        }
        
        // Trigger ragdoll death animation if player model exists
        if (this.playerModel && typeof this.playerModel.startDeathAnimation === 'function') {
            this.playerModel.startDeathAnimation();
        }
        
        // Create death message
        const deathMessage = document.createElement('div');
        deathMessage.style.cssText = `
            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            color: #f00;
            font-family: Arial, sans-serif;
            font-size: 48px;
            font-weight: bold;
            text-shadow: 2px 2px 4px #000;
            z-index: 2000;
        `;
        deathMessage.textContent = "GAME OVER";
        document.body.appendChild(deathMessage);
        
        // Add play again button after a delay
        setTimeout(() => {
            const playAgainButton = document.createElement('button');
            playAgainButton.style.cssText = `
                position: fixed;
                top: 60%;
                left: 50%;
                transform: translate(-50%, -50%);
                padding: 10px 20px;
                background-color: #333;
                color: white;
                border: 2px solid #666;
                border-radius: 5px;
                font-size: 20px;
                cursor: pointer;
                z-index: 2001;
            `;
            playAgainButton.textContent = "Play Again";
            playAgainButton.onclick = () => this.respawn();
            document.body.appendChild(playAgainButton);
        }, 2000);
    }
    
    // Load a new world with a new seed
    respawn() {
        // Generate a new random seed
        const newSeed = Math.floor(Math.random() * 1000000);
        
        // Update URL with the new seed 
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.set('seed', newSeed);
        
        // Remove any refresh parameter to ensure clean load
        newUrl.searchParams.delete('refresh');
        
        // Reset island type by removing it from URL and window
        newUrl.searchParams.delete('islandType');
        window.selectedIslandType = undefined;
        
        // Update the URL
        window.history.replaceState({}, '', newUrl.toString());
        
        // Reload the page to start a new game
        window.location.reload();
    }
    
    // For healing the player
    heal(amount) {
        this.currentHealth = Math.min(this.maxHealth, this.currentHealth + amount);
        this.updateHealthUI();
        console.log(`Player healed ${amount}. Health: ${this.currentHealth}/${this.maxHealth}`);
    }

    /**
     * Add a specific amount of health to the player
     * @param {number} amount - Amount of health to add
     */
    addHealth(amount) {
        this.heal(amount);
    }

    /**
     * Set the player's health to maximum
     */
    setFullHealth() {
        this.currentHealth = this.maxHealth;
        this.updateHealthUI();
        console.log(`Player health restored to maximum: ${this.currentHealth}/${this.maxHealth}`);
    }

    update(deltaTime, cameraForward) {
        if (!this.mesh) return;
        
        // Skip updates if player is dead
        if (this.isDead) return;
        
        // Reset debug flags
        this.debugBlocked = false;
        this.blockReason = "";
        
        // Calculate movement based on camera direction
        const moveDir = new THREE.Vector3(0, 0, 0);
        
        if (!cameraForward) {
            console.warn("No camera forward vector provided");
            return;
        }
        
        // Create normalized vectors for movement based on camera direction
        const forward = new THREE.Vector3(cameraForward.x, 0, cameraForward.z).normalize();
        const right = new THREE.Vector3(-forward.z, 0, forward.x);
        
        // Store the camera forward for player model orientation
        this.facingDirection.copy(forward);

        // Fix left/right movement directions (they were previously inverted)
        if (this.moveState.forward) moveDir.add(forward);   
        if (this.moveState.backward) moveDir.sub(forward);  
        if (this.moveState.left) moveDir.sub(right);        // LEFT: subtract right vector
        if (this.moveState.right) moveDir.add(right);       // RIGHT: add right vector

        // Determine if player is moving
        const isNowMoving = moveDir.length() > 0;
        
        // Apply movement if input detected
        if (isNowMoving) {
            moveDir.normalize();
            
            // Get current position
            const currentX = this.mesh.position.x;
            const currentZ = this.mesh.position.z;
            
            // Calculate proposed step size
            const stepSize = this.moveSpeed * deltaTime * 60;
            const clampedStepSize = Math.min(stepSize, this.maxStepDistance);
            
            // Try moving in X direction first
            if (Math.abs(moveDir.x) > 0.01) {
                const newX = currentX + moveDir.x * clampedStepSize;
                this.tryMoveToPosition('x', newX, currentZ);
            }
            
            // Try moving in Z direction next
            if (Math.abs(moveDir.z) > 0.01) {
                const newZ = currentZ + moveDir.z * clampedStepSize;
                this.tryMoveToPosition('z', this.mesh.position.x, newZ);
            }
        }
        
        // Check if movement state changed for animation
        if (isNowMoving !== this.isMoving) {
            this.isMoving = isNowMoving;
            
            // Update player model animation state if available
            if (this.playerModel) {
                this.playerModel.setAnimationState({
                    walking: this.isMoving,
                    idle: !this.isMoving
                });
            }
        }
        
        // Update player model facing direction if available
        if (this.playerModel && moveDir.length() > 0) {
            // Update facing direction using the move direction
            this.playerModel.lookAt(moveDir);
        } else if (this.playerModel) {
            // If not moving, use camera forward vector for player orientation
            const cameraForward = new THREE.Vector3();
            if (window.camera) {
                window.camera.getWorldDirection(cameraForward);
                // Only use the XZ components for direction
                cameraForward.y = 0;
                cameraForward.normalize();
                this.playerModel.lookAt(cameraForward);
            }
        }
        
        // Apply smoothed gravity to prevent camera shake
        this.applyGravitySmoothed(deltaTime);
        
        // Update player model animations if available
        if (this.playerModel) {
            this.playerModel.update(deltaTime);
        }
        
        // Sync physics body to mesh
        if (this.body) {
            this.body.position.copy(this.mesh.position);
            this.body.quaternion.copy(this.mesh.quaternion);
        }
        
        // Store current position for next frame
        this.lastPosition.copy(this.mesh.position);
        
        // Update health UI effects (flash fade, etc)
        if (performance.now() - this.lastDamageTime > 200) {
            this.updateHealthUI();
        }
    }
    
    // Try to move player to a new position with full collision handling
    tryMoveToPosition(axis, newX, newZ) {
        if (!window.terrainCollision) return false;
        
        const currentX = this.mesh.position.x;
        const currentY = this.mesh.position.y;
        const currentZ = this.mesh.position.z;
        
        // Calculate target position, handling X or Z movement
        const targetX = axis === 'x' ? newX : currentX;
        const targetZ = axis === 'z' ? newZ : currentZ;

        // Get terrain heights
        const currentHeight = window.terrainCollision.getCollisionHeight(currentX, currentZ);
        const targetHeight = window.terrainCollision.getCollisionHeight(targetX, targetZ);
        const heightDifference = targetHeight - currentHeight;

        // Check if we can step up
        let canStepUp = false;
        if (heightDifference > 0 && heightDifference <= this.maxAutoClimbHeight) {
            canStepUp = true;
        }

        // Calculate check height based on whether we're stepping up
        const checkHeight = canStepUp ? currentY + heightDifference : currentY;

        // Check target position with strict collision at appropriate height
        const targetCollision = window.terrainCollision.checkStrictCollision(
            targetX,
            checkHeight,
            targetZ
        );

        // If target position would cause collision and we're not stepping up, block movement
        if (targetCollision.collision && !canStepUp) {
            this.debugBlocked = true;
            this.blockReason = targetCollision.isHeadCollision ? 
                "Head collision" : 
                `Collision at (${targetCollision.collisionPoint?.x.toFixed(1)}, ${targetCollision.collisionPoint?.y.toFixed(1)}, ${targetCollision.collisionPoint?.z.toFixed(1)})`;
            return false;
        }

        // Check intermediate points to prevent tunneling
        const steps = 4;
        for (let i = 1; i < steps; i++) {
            const fraction = i / steps;
            const intermediateX = currentX + (targetX - currentX) * fraction;
            const intermediateZ = currentZ + (targetZ - currentZ) * fraction;
            
            // Interpolate height for intermediate points
            const intermediateTargetHeight = currentHeight + (heightDifference * fraction);
            const intermediateCheckHeight = canStepUp ? currentY + (heightDifference * fraction) : currentY;
            
            const intermediateCollision = window.terrainCollision.checkStrictCollision(
                intermediateX,
                intermediateCheckHeight,
                intermediateZ
            );
            
            if (intermediateCollision.collision && !canStepUp) {
                this.debugBlocked = true;
                this.blockReason = "Collision detected during movement";
                return false;
            }
        }

        // All checks passed - perform the movement
        if (axis === 'x') this.mesh.position.x = targetX;
        if (axis === 'z') this.mesh.position.z = targetZ;

        // If stepping up, adjust height immediately
        if (canStepUp) {
            this.mesh.position.y = targetHeight + this.footOffset;
            this._canJump = true;
        }

        return true;
    }
    
    // Keep player properly aligned with terrain
    applyGravitySmoothed(deltaTime) {
        if (!window.terrainCollision) return;
        
        try {
            // Get current ground height from collision system
            const groundHeight = window.terrainCollision.getCollisionHeight(
                this.mesh.position.x, 
                this.mesh.position.z
            );
            
            // Guard against invalid height data
            if (!isFinite(groundHeight)) {
                console.warn("Invalid ground height detected");
                return;
            }
            
            // Check for upcoming terrain change in movement direction
            this.checkForUpcomingStep();
            
            // Calculate target height with foot offset
            this.targetY = groundHeight + this.footOffset;
            
            // Initialize lastY if this is the first update
            if (this.lastY === 0) {
                this.lastY = this.mesh.position.y;
            }
            
            const heightDifference = this.mesh.position.y - this.targetY;
            const now = performance.now();
            
            // Check if player needs to jump to navigate terrain
            if (heightDifference < -0.5 && heightDifference > -this.maxAutoClimbHeight && 
                now - this.lastJumpTime > this.jumpCooldown) {
                
                // If we're approaching a step, let the prediction handle it
                if (!this.isApproachingStep) {
                    this.performAutoJump(this.targetY);
                }
            }
            
            // Handle jump state if we're currently jumping
            if (this.jumpState.jumping) {
                this.updateJumpInterpolation(now);
                return; // Skip normal gravity during jump animation
            }
            
            // Handle jump prediction for upcoming terrain
            if (this.isApproachingStep && heightDifference < 0.2) {
                this.performAutoJump(this.predictedStepHeight + this.footOffset);
                this.isApproachingStep = false;
                return;
            }
            
            // Handle different vertical movement cases
            if (heightDifference > 1.0) {
                // We're significantly above ground - apply falling with damping
                const fallSpeed = Math.min(0.3, 0.15 + (heightDifference * 0.05));
                this.mesh.position.y = Math.max(
                    this.targetY,
                    this.mesh.position.y - (fallSpeed * deltaTime * 60)
                );
                this._canJump = false;
            } else if (heightDifference < -0.1) {
                // We're below target - move up with smoothing
                const riseSpeed = Math.min(0.2, Math.abs(heightDifference) * 0.1);
                this.mesh.position.y = THREE.MathUtils.lerp(
                    this.mesh.position.y,
                    this.targetY,
                    riseSpeed * deltaTime * 20
                );
                this._canJump = true;
            } else {
                // We're close to target - smooth interpolation
                const smoothFactor = Math.min(0.2, this.positionSmoothing * deltaTime * 30);
                this.mesh.position.y = THREE.MathUtils.lerp(
                    this.mesh.position.y,
                    this.targetY,
                    smoothFactor
                );
                this._canJump = Math.abs(heightDifference) < 0.5;
            }
            
            // Update lastY for next frame
            this.lastY = this.mesh.position.y;
        } catch (error) {
            console.error("Error in applyGravitySmoothed:", error);
        }
    }
    
    // Check for upcoming steps or changes in terrain height
    checkForUpcomingStep() {
        // Get movement direction
        const moveDir = new THREE.Vector3(0, 0, 0);
        let isMoving = false;
        
        if (this.moveState.forward) { moveDir.z -= 1; isMoving = true; }
        if (this.moveState.backward) { moveDir.z += 1; isMoving = true; }
        if (this.moveState.left) { moveDir.x -= 1; isMoving = true; }
        if (this.moveState.right) { moveDir.x += 1; isMoving = true; }
        
        // Only check if we're moving
        if (!isMoving || !window.terrainCollision) {
            this.isApproachingStep = false;
            return;
        }
        
        // Normalize the direction
        moveDir.normalize();
        
        // Get rotation from the mesh if available
        if (this.mesh && this.mesh.quaternion) {
            // Transform movement direction based on player rotation
            moveDir.applyQuaternion(this.mesh.quaternion);
        }
        
        // Check multiple points ahead
        const currentHeight = window.terrainCollision.getCollisionHeight(
            this.mesh.position.x, 
            this.mesh.position.z
        );
        
        // Check 3 points ahead at different distances
        const checkDistances = [1.0, 1.5, 2.0];
        let stepDetected = false;
        
        for (const distance of checkDistances) {
            const aheadX = this.mesh.position.x + moveDir.x * distance;
            const aheadZ = this.mesh.position.z + moveDir.z * distance;
            const aheadHeight = window.terrainCollision.getCollisionHeight(aheadX, aheadZ);
            
            const heightDifference = aheadHeight - currentHeight;
            
            // If we detect a step within climbable range
            if (heightDifference > 0.3 && heightDifference <= this.maxAutoClimbHeight) {
                this.isApproachingStep = true;
                this.predictedStepHeight = aheadHeight;
                stepDetected = true;
                break;
            }
        }
        
        // Reset if no step detected
        if (!stepDetected) {
            this.isApproachingStep = false;
        }
    }
    
    // Perform an auto-jump with smooth interpolation
    performAutoJump(targetHeight) {
        const now = performance.now();
        
        // Don't auto-jump too frequently
        if (now - this.lastJumpTime < this.jumpCooldown) {
            return;
        }
        
        // Start a new jump
        this.jumpState.jumping = true;
        this.jumpState.startTime = now;
        this.jumpState.startY = this.mesh.position.y;
        this.jumpState.targetY = targetHeight;
        this.jumpState.jumpType = 'auto';
        this.lastJumpTime = now;
        
        // Notify player model of jump if available
        if (this.playerModel && typeof this.playerModel.setAnimationState === 'function') {
            this.playerModel.setAnimationState({
                jumping: true
            });
        }
        
        // Notify camera of jump state
        this.notifyCameraOfJump(true);
        
        this._canJump = false;
    }
    
    // Notify camera about jump state changes
    notifyCameraOfJump(isJumping) {
        // Check if we have a reference to the camera controller
        if (window.cameraController && typeof window.cameraController.setJumpState === 'function') {
            window.cameraController.setJumpState(isJumping);
        }
    }
    
    // Update the jump interpolation
    updateJumpInterpolation(now) {
        const elapsed = now - this.jumpState.startTime;
        const duration = this.jumpState.duration;
        
        // Calculate jump progress (0 to 1)
        let progress = Math.min(1.0, elapsed / duration);
        
        // Apply easing function for smoother jump arc
        // Use sine curve for jumping (accelerates at start, decelerates at end)
        const easedProgress = Math.sin(progress * Math.PI / 2);
        
        // Interpolate position
        this.mesh.position.y = THREE.MathUtils.lerp(
            this.jumpState.startY,
            this.jumpState.targetY,
            easedProgress
        );
        
        // If jump is complete
        if (progress >= 1.0) {
            this.jumpState.jumping = false;
            
            // Reset player model animation if applicable
            if (this.playerModel && typeof this.playerModel.setAnimationState === 'function') {
                this.playerModel.setAnimationState({
                    jumping: false
                });
            }
            
            // Notify camera jump is complete
            this.notifyCameraOfJump(false);
            
            // Allow manual jumping again after auto-jump completes
            setTimeout(() => {
                this._canJump = true;
            }, 100);
        }
    }

    // Improve the jump method to handle variable-height climbing with smooth animation
    jump() {
        if (!this._canJump) return;
        
        const now = performance.now();
        
        // Don't jump too frequently
        if (now - this.lastJumpTime < this.jumpCooldown) {
            return;
        }
        
        // Get terrain height and current height
        const groundHeight = window.terrainCollision.getCollisionHeight(
            this.mesh.position.x, 
            this.mesh.position.z
        );
        
        // Look ahead in movement direction to see if we need to jump onto something
        let climbTarget = null;
        const forward = new THREE.Vector3();
        
        // Get forward direction from player rotation
        forward.set(0, 0, -1).applyQuaternion(this.mesh.quaternion);
        
        // Check height 1 block ahead
        const aheadX = this.mesh.position.x + forward.x * 1.5;
        const aheadZ = this.mesh.position.z + forward.z * 1.5;
        const aheadHeight = window.terrainCollision.getCollisionHeight(aheadX, aheadZ);
        
        // If there's a climb-able obstacle ahead
        if (aheadHeight > groundHeight && 
            aheadHeight - groundHeight <= this.maxManualClimbHeight) {
            // Set jump height to clear the obstacle
            climbTarget = aheadHeight + 1.0;
        }
        
        // Set up the jump state
        this.jumpState.jumping = true;
        this.jumpState.startTime = now;
        this.jumpState.startY = this.mesh.position.y;
        this.jumpState.jumpType = 'manual';
        this.lastJumpTime = now;
        
        if (climbTarget) {
            // Jump to clear specific obstacle
            this.jumpState.targetY = climbTarget;
            this.jumpState.duration = 400; // Slightly longer for higher jumps
        } else {
            // Standard jump
            this.jumpState.targetY = this.mesh.position.y + this.jumpHeight;
            this.jumpState.duration = 300;
        }
        
        // Notify player model of jump if available
        if (this.playerModel && typeof this.playerModel.setAnimationState === 'function') {
            this.playerModel.setAnimationState({
                jumping: true
            });
        }
        
        // Notify camera of jump state
        this.notifyCameraOfJump(true);
        
        this._canJump = false;
    }

    canJump() {
        return this._canJump;
    }

    getMesh() {
        return this.mesh;
    }

    getBody() {
        return this.body;
    }
    
    handleCollision() {
        this._canJump = true;
    }
    
    isMovementBlocked() {
        return this.debugBlocked;
    }

    getBlockReason() {
        return this.blockReason;
    }
    
    // Initialize player with default equipment
    initializeDefaultEquipment() {
        if (!this.scene) {
            console.error("Cannot initialize equipment: scene is not available");
            return;
        }
        
        console.log("Initializing default equipment...");
        
        // Create default pistol in slot 0
        const pistol = new Pistol(this.scene, this);
        this.inventory.addItem(pistol, 0);
        
        // Make sure the first slot is equipped
        this.inventory.setEquippedIndex(0);
        
        // Initialize punch attack as secondary option
        this.punchAttack = new PunchAttack(this.scene, this);
        
        // Connect punch attack to strength skill
        this.punchAttack.setStrengthSkill(this.strengthSkill);
        
        console.log("Default equipment initialized");
    }
    
    // Set scene reference (can be called after constructor)
    setScene(scene) {
        this.scene = scene;
        this.initializeDefaultEquipment();
    }
    
    // Cycle through inventory slots
    cycleInventory() {
        if (this.inventory) {
            // Unequip current item if it has an unequip method
            const currentItem = this.inventory.getEquipped();
            if (currentItem && typeof currentItem.unequip === 'function') {
                currentItem.unequip();
                
                // Ensure player model is not in aiming state
                if (this.playerModel) {
                    this.playerModel.setAiming(false);
                }
            }
            
            // Move to next item
            this.inventory.cycleNext();
            
            // Equip the new item if it has an equip method
            const newItem = this.inventory.getEquipped();
            if (newItem && typeof newItem.equip === 'function') {
                // Small delay to ensure previous item is fully unequipped
                setTimeout(() => {
                    newItem.equip();
                }, 50);
            }
            
            // Return the newly equipped item for UI updates
            return newItem;
        }
        return null;
    }
    
    // Get current inventory for UI
    getInventory() {
        return this.inventory;
    }
    
    // Handle weapon firing
    handleFire() {
        console.log("PlayerController.handleFire called");
        if (!this.inventory) {
            console.log("No inventory system found.");
            return false;
        }

        const item = this.inventory.getEquipped();
        console.log("Current equipped item:", item ? item.name : "nothing");
        
        // If no item is equipped, use punch attack
        if (!item) {
            console.log("No item in selected slot, using punch attack");
            return this.handlePunchAttack();
        }
        
        console.log("Attempting to fire:", item ? item.name : "nothing", 
                    "equipped:", item ? item.equipped : false,
                    "isPistol:", item instanceof Pistol);
                    
        if (!(item instanceof Pistol)) {
            console.log("Equipped item is not a pistol");
            return false;
        }
        
        if (!item.equipped) {
            console.log("Pistol is not properly equipped");
            return false;
        }
        
        // If we have a player model, get the weapon attachment point and trigger animation
        if (this.playerModel) {
            // Get the world position and orientation of the weapon attachment point
            const weaponAttachment = this.playerModel.getWeaponAttachmentPoint();
            if (weaponAttachment) {
                // Get position
                const weaponWorldPosition = new THREE.Vector3();
                weaponAttachment.getWorldPosition(weaponWorldPosition);
                
                // Get orientation
                const weaponWorldQuaternion = new THREE.Quaternion();
                weaponAttachment.getWorldQuaternion(weaponWorldQuaternion);
                
                // Set pistol at weapon attachment point with proper orientation
                item.setWeaponAttachmentPoint(weaponWorldPosition, weaponWorldQuaternion);
                
                // Trigger the shooting animation
                this.playerModel.triggerShootingAnimation();
            }
        }
        
        console.log("Pistol equipped and ready to fire");
        return item.fire(this); // Pass `this` (PlayerController instance) as the player argument
    }
    
    /**
     * Handle punch attack when no weapon is equipped
     * @returns {boolean} True if punch was performed successfully
     */
    handlePunchAttack() {
        console.log("PlayerController.handlePunchAttack called");
        console.log("this.scene exists:", !!this.scene);
        console.log("this.strengthSkill exists:", !!this.strengthSkill);
        
        // Ensure the punch attack is initialized
        if (!this.punchAttack) {
            console.log("Initializing punch attack for the first time");
            // Lazy initialize if not already done
            this.punchAttack = new PunchAttack(this.scene, this);
            this.punchAttack.setStrengthSkill(this.strengthSkill);
        } else {
            console.log("Punch attack already initialized");
        }
        
        // Check if punch is on cooldown
        if (this.punchAttack.isOnCooldown()) {
            console.log("Punch attack is on cooldown");
            return false;
        }
        
        console.log(`Performing punch attack with ${this.punchAttack.getDamage()} damage`);
        
        // Perform the punch attack
        const result = this.punchAttack.fire();
        console.log("Punch attack result:", result);
        return result;
    }

    // Get current strength skill level (for UI or debugging)
    getStrengthLevel() {
        return this.strengthSkill ? this.strengthSkill.level : 1;
    }
    
    // Get the player model if available
    getPlayerModel() {
        return this.playerModel;
    }
    
    /**
     * Set the aiming state of the player
     * @param {boolean} isAiming - Whether the player is currently aiming
     */
    setAiming(isAiming) {
        // Update player model aiming state
        if (this.playerModel) {
            this.playerModel.setAiming(isAiming);
        }
        
        // Update equipped item aiming state
        const equippedItem = this.inventory?.getEquipped();
        if (equippedItem && typeof equippedItem.setAiming === 'function') {
            equippedItem.setAiming(isAiming);
        }
        
        // Update camera zoom for aiming
        if (window.cameraController && typeof window.cameraController.setAiming === 'function') {
            window.cameraController.setAiming(isAiming);
        }
    }

    setMovementState(state) {
        this.moveState = state;
    }

    /**
     * Set direct movement input (used by mobile controls)
     * @param {number} forward - Forward/backward input value (-1 to 1)
     * @param {number} right - Right/left input value (-1 to 1)
     */
    setMoveInput(forward, right) {
        // Set movement state based on input values
        this.moveState.forward = forward > 0.2;
        this.moveState.backward = forward < -0.2;
        this.moveState.right = right > 0.2;
        this.moveState.left = right < -0.2;
        
        // Store raw input values for analog movement if needed
        this.rawMoveInput = { forward, right };
    }
    
    /**
     * Start attacking (used by mobile controls)
     */
    startAttack() {
        // Trigger the appropriate attack based on equipped item
        const equippedItem = this.inventory.getEquipped();
        
        if (equippedItem) {
            if (equippedItem.name === "Pistol") {
                equippedItem.startFiring();
            } else {
                // Trigger melee attack if no ranged weapon equipped
                this.handlePunchAttack();
            }
        } else {
            // Default to punch if nothing equipped
            this.handlePunchAttack();
        }
    }
    
    /**
     * Stop attacking (used by mobile controls)
     */
    stopAttack() {
        // Stop the appropriate attack based on equipped item
        const equippedItem = this.inventory.getEquipped();
        
        if (equippedItem) {
            if (equippedItem.name === "Pistol") {
                equippedItem.stopFiring();
            }
        }
    }
    
    /**
     * Start aiming (used by mobile controls)
     */
    startAiming() {
        this.setAiming(true);
    }
    
    /**
     * Stop aiming (used by mobile controls)
     */
    stopAiming() {
        this.setAiming(false);
    }
    
    /**
     * Rotate the player/camera (used by mobile controls)
     * @param {number} deltaX - Change in X (horizontal)
     * @param {number} deltaY - Change in Y (vertical)
     */
    rotate(deltaX, deltaY) {
        // This will be handled by the camera controller
        // Just pass the event to any connected camera
        if (window.camera && typeof window.camera.onMobileRotation === 'function') {
            window.camera.onMobileRotation(deltaX, deltaY);
        }
    }
    
    /**
     * Update attack state (called every frame when attack button is held)
     */
    updateAttack() {
        // Handle continuous fire for automatic weapons if needed
        const equippedItem = this.inventory.getEquipped();
        
        if (equippedItem && equippedItem.name === "Pistol" && typeof equippedItem.updateFiring === 'function') {
            equippedItem.updateFiring();
        }
    }
    
    /**
     * Update aiming state (called every frame when aim button is held)
     */
    updateAiming() {
        // Nothing additional needed here as aiming is a toggle state
    }
}
